import { useEffect, useState } from "react";
import API from "@/lib/api";
import { TrendingDown, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * "Saved $X this year" hero widget for the patient dashboard.
 *
 * Pulls from /api/profile/savings-summary, which sums up paid
 * payment_transactions for the current calendar year and compares them to a
 * conservative retail cash-pay estimate (~$968-$1069 / 30-day vial). Renders
 * nothing if the patient hasn't paid yet — keeps the dashboard clean for new
 * users instead of showing "$0 saved" awkwardness.
 *
 * Includes a Share button that copies a friendly referral message + the
 * patient's referral URL so the moment doubles as a low-friction referral nudge.
 */
export default function SavingsWidget({ user }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    API.get("/profile/savings-summary").then(({ data: d }) => {
      if (!cancelled) setData(d);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.paid_orders || data.savings_dollars <= 0) return null;

  const referralCode = user?.referral_code || "";
  const referralUrl = referralCode
    ? `${typeof window !== "undefined" ? window.location.origin : "https://glpflo.org"}/register?ref=${referralCode}`
    : `${typeof window !== "undefined" ? window.location.origin : "https://glpflo.org"}/register`;

  const share = async () => {
    const message = `I've saved $${Math.round(data.savings_dollars).toLocaleString()} on my GLP-1s this year through GLPFlo. Use my link and we both get $20 off: ${referralUrl}`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "I'm saving on GLP-1s with GLPFlo", text: message, url: referralUrl });
        return;
      } catch { /* user cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Share message copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const formatted = `$${Number(data.savings_dollars).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-2xl p-6 mb-8 text-white shadow-[0_10px_40px_-10px_rgba(16,185,129,0.5)]"
      data-testid="savings-widget"
    >
      {/* Decorative glow */}
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-teal-300/20 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-50/90">
              Saved in {data.year}
            </span>
          </div>
          <p className="text-4xl sm:text-5xl font-black leading-none" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="savings-amount">
            {formatted}
          </p>
          <p className="text-sm text-emerald-50/90 mt-2 max-w-md">
            That&apos;s how much less you&apos;ve spent on your GLP-1 with GLPFlo this year compared to the average retail cash price.
            <span className="block text-xs text-emerald-100/70 mt-1">
              Across {data.paid_orders} paid order{data.paid_orders === 1 ? "" : "s"} · You paid ${data.you_paid.toLocaleString()} (retail est. ${data.retail_estimate.toLocaleString()}).
            </span>
          </p>
        </div>
        <button
          onClick={share}
          className="self-start sm:self-center inline-flex items-center gap-2 bg-white text-emerald-700 font-semibold rounded-full px-5 py-3 text-sm hover:bg-emerald-50 hover:shadow-lg active:scale-95 transition-all"
          data-testid="savings-share-btn"
        >
          <Share2 className="w-4 h-4" /> Share my savings
        </button>
      </div>
    </div>
  );
}
